'use client';

import type { SwitchProps } from '@magicschool/spellbook/components/Switch';
import { Switch } from '@magicschool/spellbook/components/Switch';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type SwitchGroupOption = SwitchProps & {
  value: string;
  label: string;
};

export type SwitchGroupProps = React.ComponentProps<'div'> &
  VariantProps<typeof switchGroupVariants> & {
    /** The list of switch options to render. Each option maps to an individual `Switch` with `value`, `label`, and optional `description` or `disabled`. */
    options: SwitchGroupOption[];
    /** Controlled toggled values. An array of option `value` strings that are currently on. */
    value?: string[];
    /** Initial toggled values for uncontrolled usage. An array of option `value` strings that are on at mount. */
    defaultValue?: string[];
    /** Callback fired when any switch in the group changes. Receives the updated array of toggled `value` strings. */
    onCheckedChange?: (value: string[]) => void;
    /** Disables all switches in the group. Individual options can also set their own `disabled`. */
    disabled?: boolean;
    /** Marks the group as required for form validation. */
    required?: boolean;
    /** Marks all switches in the group as invalid and sets `aria-invalid="true"`. Use with form validation. */
    invalid?: boolean;
    /** The shared `name` attribute for all switches in the group, used for form submission. Auto-generated if not provided. */
    name?: string;
    /** The `id` of an external label element to associate with the group via `aria-labelledby`. */
    labelId?: string;
  };

const switchGroupVariants = cva('flex gap-2 w-full', {
  variants: {
    orientation: {
      horizontal: 'flex-row flex-wrap row-gap-4',
      vertical: 'flex-col',
    },
    position: {
      start: '',
      end: '',
    },
  },
});

/**
 * Groups multiple toggle switches under a shared layout with managed multi-toggle state. Renders individual `Switch` components from an `options` array.
 *
 * `SwitchGroup` is primarily an organizational wrapper — it provides consistent layout, shared `disabled`/`invalid` state, and a
 * unified `onCheckedChange` callback, but each switch is conceptually an independent toggle (e.g., individual notification settings).
 * This differs from `CheckboxGroup`, which represents a single conceptual field where all items are choices from one question
 * (e.g., "select your interests"). Both groups assign a shared `name` to all children and manage an array of selected values,
 * but the semantic intent is different: `CheckboxGroup` = "pick from this list", `SwitchGroup` = "configure these settings".
 *
 * ### When to use
 * - Toggling multiple independent settings at once (e.g., notification preferences, feature flags)
 * - When each option represents an immediate on/off toggle and all should be visible simultaneously
 * - Settings panels where switches need consistent layout and shared disabled/invalid state
 *
 * ### When NOT to use
 * - For multi-select choices confirmed by a separate action (e.g., a save/submit button): use `CheckboxGroup` instead
 * - For a single on/off toggle: use `Switch` directly
 * - For selecting from a long list with search: use `MultiSelect` instead
 *
 * ### API
 * - `options`: Array of `SwitchGroupOption` objects, each rendered as a `Switch`. Supports per-option `label`, `value`, `description`, and `disabled`.
 * - `value` / `defaultValue`: Controlled or uncontrolled toggled values as an array of `value` strings.
 * - `onCheckedChange`: Callback with the updated array of toggled `value` strings.
 * - `orientation`: Layout direction: `'vertical'` (default) stacks switches, `'horizontal'` arranges them in a wrapping row.
 * - `position`: Controls switch/label order for all children: `'start'` (default) places switches before labels, `'end'` places labels first with switches right-aligned.
 * - `disabled`: Disables all switches. Individual options can also be disabled independently.
 * - `invalid`: Sets `aria-invalid="true"` on all switches and applies error border styling.
 * - `labelId`: Links an external label to the group via `aria-labelledby`.
 *
 * ### Visual states
 * - **Disabled**: All switches render as muted and non-interactive when group-level `disabled` is set
 * - **Invalid**: All switches show error border styling when group-level `invalid` is set
 *
 * ### Accessibility
 * - Renders as a `<div role="group">` to semantically group the switches
 * - Use `labelId` to link an external label via `aria-labelledby` — without it, the group has no accessible name
 * - All switches share the same `name` attribute for submission
 *
 * See the [Checkbox documentation](?path=/docs/components-checkbox--docs) for a comparison table of all selection components.
 */
export const SwitchGroup: React.FC<SwitchGroupProps> = ({
  options = [],
  value,
  defaultValue,
  labelId,
  onCheckedChange,
  disabled = false,
  invalid = false,
  name: groupName,
  className,
  orientation = 'vertical',
  position = 'start',
  ...props
}) => {
  const id = useId(groupName);
  const name = groupName || id;

  const handleOnCheckedChange = (checked: boolean, option: SwitchGroupOption) => {
    const newValue = checked ? [...(value || []), option.value] : (value || []).filter((v) => v !== option.value);
    if (onCheckedChange) {
      onCheckedChange(newValue);
    }
  };

  return (
    <div
      role="group"
      data-spellbook-name="switch-group"
      className={cn(switchGroupVariants({ orientation }), className)}
      id={id}
      {...(labelId && { 'aria-labelledby': labelId })}
      {...props}
    >
      {options.map((option, index) => {
        const switchId = `${id}-${index}`;
        const handleOptionCheckedChange = (checked: boolean) => {
          handleOnCheckedChange(Boolean(checked), option);
        };

        return (
          <Switch
            key={switchId}
            id={switchId}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description}
            disabled={disabled || option.disabled}
            invalid={invalid}
            checked={value?.includes(option.value)}
            defaultChecked={defaultValue?.includes(option.value)}
            onCheckedChange={handleOptionCheckedChange}
            position={position}
            groupOrientation={orientation}
          />
        );
      })}
    </div>
  );
};
